export const downloadBlob = (blob: Blob, filename: string): void => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  // Diferido: Firefox aborta la descarga si el object URL se revoca en el mismo tick.
  setTimeout(() => URL.revokeObjectURL(url), 0);
};

/**
 * Nombre de archivo de un Content-Disposition. El formato RFC 5987 (`filename*=UTF-8''`)
 * va primero: si se busca `filename=` primero, matchea adentro del `filename*=` y devuelve
 * `*=UTF-8''archivo.csv` como nombre.
 *
 * Devuelve undefined tambien cuando el header no llega: es cross-origin, asi que el
 * backend tiene que exponerlo con `Access-Control-Expose-Headers: Content-Disposition`.
 */
export const parseContentDispositionFilename = (
  disposition: string | null
): string | undefined => {
  if (!disposition) {
    return undefined;
  }

  const encoded = disposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (encoded?.[1]) {
    try {
      return decodeURIComponent(encoded[1].trim());
    } catch {
      return encoded[1].trim();
    }
  }

  const plain =
    disposition.match(/filename="([^"]+)"/i) ?? disposition.match(/filename=([^;]+)/i);
  return plain?.[1]?.trim() || undefined;
};

const ANALYTICS_EXPORT_LABELS: Record<string, string> = {
  "events-report": "Eventos",
  "inscripciones-semanal": "Inscripciones semanales",
  "inscripciones-mensual": "Inscripciones mensuales",
  "nuevos-usuarios-semanal": "Nuevos usuarios semanales",
  "nuevos-usuarios-mensual": "Nuevos usuarios mensuales",
  "recurrencia-mensual": "Recurrencia mensual",
  "usuarios-activos": "Usuarios activos",
  "canjes-beneficios": "Canjes de beneficios",
  "canjes-puntos": "Canjes de puntos",
  "cancelaciones": "Historial de cancelaciones",
};

export const buildAnalyticsExportFilename = (
  filename: string,
  tenants: readonly { value: string; label: string }[],
  requestedFilters?: { tenantId: string | null; from: string; to: string }
): string => {
  const filteredFilename = filename === "cancelaciones.csv" && requestedFilters
    ? `cancelaciones_${requestedFilters.tenantId ?? "todos-los-tenants"}_${requestedFilters.from}_${requestedFilters.to}.csv`
    : filename;
  const match = filteredFilename.match(/^([^_]+)_([^_]+)_(\d{4}-\d{2}-\d{2}|inicio)_(\d{4}-\d{2}-\d{2}|fin)(?:_(start_date|create_date)-(asc|desc))?\.(csv|xlsx)$/);
  if (!match || !ANALYTICS_EXPORT_LABELS[match[1]]) return filename;
  const [, prefix, tenantPart, from, to, sortField, sortDirection, extension] = match;
  const tenant = tenantPart === "todos-los-tenants"
    ? "Todos los tenants"
    : tenants.find((option) => option.value === tenantPart)?.label ?? tenantPart.replace(/-/g, " ");
  // Los caracteres de control son ilegales en un nombre de archivo, por eso van en la clase.
  // eslint-disable-next-line no-control-regex
  const safeTenant = tenant.replace(/[\\/:*?"<>|\u0000-\u001f]/g, " ").replace(/\s+/g, " ").trim();
  const formatDay = (day: string): string => day.split("-").reverse().join("-");
  const range = from === "inicio" && to === "fin"
    ? "Todo el periodo"
    : `${from === "inicio" ? "Inicio" : formatDay(from)} al ${to === "fin" ? "Fin" : formatDay(to)}`;
  const order = sortField
    ? ` - ${sortField === "create_date" ? "Creacion" : "Realizacion"} ${sortDirection}`
    : "";
  return `${ANALYTICS_EXPORT_LABELS[prefix]} - ${safeTenant} - ${range}${order}.${extension}`;
};
